/**
 * Plain pilot fields — the free-text leaves, the background pair, and `set_level`, whose four
 * budget walls plus the LL0 per-key rank ceiling are the reason it is not a plain setter.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type Result } from "@lancer-vtt/shared";
import { HASE_BUDGET_BASE, MAX_TRIGGER_RANK, SKILL_BUDGET_BASE, TALENT_BUDGET_BASE, maxHeldRank, rankCeilingAtLevel, rankSum } from "../budgets";
import type { RosterLeafCommand } from "../commands";
import { spentLicenseRanks } from "../licenses";
import { CHOSEN_DAMAGE_TYPES, type PilotData, type RosterState } from "../types";
import { haseSum } from "./allocation";
import { MAX_LEVEL, MIN_LEVEL, changed, requirePilot, withPilot, type RosterApplyResult } from "./context";

export function applySetPilotText(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "set_pilot_text" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  // Optional text leaves restore as "" rather than widening the command contract;
  // both forms render as unset.
  const prev = pilot[cmd.field] ?? "";
  const next: PilotData = { ...pilot, [cmd.field]: cmd.value };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "set_pilot_text",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      field: cmd.field,
      value: prev,
    },
    events: changed(cmd.pilotId),
  });
}

export function applySetPilotBondmate(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "set_pilot_bondmate" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  if (cmd.bondmate !== undefined) {
    if (cmd.bondmate === pilot.id) return err(new CommandError(`set_pilot_bondmate: ${pilot.callsign} can't be their own Bondmate`));
    const mateR = requirePilot(state, cmd.bondmate);
    if (!mateR.ok) return mateR;
  }
  const next: PilotData = { ...pilot };
  if (cmd.bondmate === undefined) delete next.bondmate;
  else next.bondmate = cmd.bondmate;
  return ok({
    state: withPilot(state, next),
    inverse: { type: "set_pilot_bondmate", actorId: cmd.actorId, pilotId: cmd.pilotId, ...(pilot.bondmate !== undefined ? { bondmate: pilot.bondmate } : {}) },
    events: changed(cmd.pilotId),
  });
}

export function applySetPilotGearDamageType(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "set_pilot_gear_damage_type" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  if (!pilot.gear.includes(cmd.gearId)) return err(new CommandError(`set_pilot_gear_damage_type: ${pilot.callsign} doesn't hold ${cmd.gearId}`));
  if (cmd.damageType !== undefined && !CHOSEN_DAMAGE_TYPES.includes(cmd.damageType)) {
    return err(new CommandError(`set_pilot_gear_damage_type: damage type must be one of ${CHOSEN_DAMAGE_TYPES.join(", ")} (got ${cmd.damageType})`));
  }
  const prior = pilot.gearDamageTypes?.[cmd.gearId];
  const { [cmd.gearId]: _old, ...rest } = pilot.gearDamageTypes ?? {};
  void _old;
  const types = cmd.damageType === undefined ? rest : { ...rest, [cmd.gearId]: cmd.damageType };
  const next: PilotData = { ...pilot };
  if (Object.keys(types).length === 0) delete next.gearDamageTypes;
  else next.gearDamageTypes = types;
  return ok({
    state: withPilot(state, next),
    inverse: { type: "set_pilot_gear_damage_type", actorId: cmd.actorId, pilotId: cmd.pilotId, gearId: cmd.gearId, ...(prior !== undefined ? { damageType: prior } : {}) },
    events: changed(cmd.pilotId),
  });
}

export function applySetPilotHp(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "set_pilot_hp" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  if (cmd.hp !== undefined && !(Number.isInteger(cmd.hp) && cmd.hp >= 0)) {
    return err(new CommandError(`set_pilot_hp: HP must be a whole number ≥ 0 (got ${cmd.hp})`));
  }
  const next: PilotData = { ...pilot };
  if (cmd.hp === undefined) delete next.currentHp;
  else next.currentHp = cmd.hp;
  return ok({
    state: withPilot(state, next),
    inverse: { type: "set_pilot_hp", actorId: cmd.actorId, pilotId: cmd.pilotId, ...(pilot.currentHp !== undefined ? { hp: pilot.currentHp } : {}) },
    events: changed(cmd.pilotId),
  });
}

/** `rest`: half the pilot's max HP back, rounded up; full is stored as absent. Inverse: `set_pilot_hp`. */
export function applyRest(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "rest" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  if (!(Number.isInteger(cmd.maxHp) && cmd.maxHp > 0)) {
    return err(new CommandError(`rest: max HP must be a whole number > 0 (got ${cmd.maxHp})`));
  }
  if (pilot.currentHp === undefined || pilot.currentHp >= cmd.maxHp) {
    return err(new CommandError(`rest: ${pilot.callsign || "the pilot"} is already at full HP`));
  }
  const hp = pilot.currentHp + Math.ceil(cmd.maxHp / 2);
  const next: PilotData = { ...pilot };
  if (hp >= cmd.maxHp) delete next.currentHp;
  else next.currentHp = hp;
  return ok({
    state: withPilot(state, next),
    inverse: { type: "set_pilot_hp", actorId: cmd.actorId, pilotId: cmd.pilotId, hp: pilot.currentHp },
    events: changed(cmd.pilotId),
  });
}

export function applySetPilotBackground(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "set_pilot_background" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  const next: PilotData = { ...pilot };
  if (cmd.name === undefined) delete next.background;
  else next.background = cmd.name;
  if (cmd.ref) next.backgroundRef = cmd.ref;
  else delete next.backgroundRef;
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "set_pilot_background",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      ...(pilot.background === undefined ? {} : { name: pilot.background }),
      ...(pilot.backgroundRef ? { ref: pilot.backgroundRef } : {}),
    },
    events: changed(cmd.pilotId),
  });
}

export function applySetLevel(
  state: RosterState,
  cmd: Extract<RosterLeafCommand, { type: "set_level" }>,
): Result<RosterApplyResult, CommandError> {
  const pilotR = requirePilot(state, cmd.pilotId);
  if (!pilotR.ok) return pilotR;
  const pilot = pilotR.value;
  if (
    !Number.isInteger(cmd.level) ||
    cmd.level < MIN_LEVEL ||
    cmd.level > MAX_LEVEL
  ) {
    return err(
      new CommandError(
        `set_level: level ${cmd.level} out of range ${MIN_LEVEL}–${MAX_LEVEL}`,
      ),
    );
  }
  // Level now drives THREE budgets, each `base + level`. Lowering level lowers
  // all three; if the pilot's *current* allocation in any of them would exceed
  // its new budget, reject — the user must lower that allocation first.
  // (Silently clamping would be a surprising, lossy mutation.) Easy to forget
  // the two new ones (gotcha) — keep all three guarded here. Below them sit the
  // license budget and the LL0 per-key rank ceiling, which the *sums* can't catch.
  const haseUsed = haseSum(pilot.mechSkills);
  const haseBudget = HASE_BUDGET_BASE + cmd.level;
  if (haseUsed > haseBudget) {
    return err(
      new CommandError(
        `set_level: HASE allocation ${haseUsed} exceeds new budget ${haseBudget} at level ${cmd.level}; lower mech skills first`,
      ),
    );
  }
  const skillUsed = rankSum(pilot.skills);
  const skillBudget = SKILL_BUDGET_BASE + cmd.level;
  if (skillUsed > skillBudget) {
    return err(
      new CommandError(
        `set_level: skill-trigger ranks ${skillUsed} exceed new budget ${skillBudget} at level ${cmd.level}; lower skill triggers first`,
      ),
    );
  }
  const talentUsed = rankSum(pilot.talents);
  const talentBudget = TALENT_BUDGET_BASE + cmd.level;
  if (talentUsed > talentBudget) {
    return err(
      new CommandError(
        `set_level: talent ranks ${talentUsed} exceed new budget ${talentBudget} at level ${cmd.level}; lower talents first`,
      ),
    );
  }
  // License ranks are budgeted 1-per-LL (GMS excluded — free, seeded). Lowering
  // level lowers this budget too; reject if the current non-GMS spend wouldn't fit.
  const licenseUsed = spentLicenseRanks(pilot);
  if (licenseUsed > cmd.level) {
    return err(
      new CommandError(
        `set_level: license ranks ${licenseUsed} exceed new budget ${cmd.level} at level ${cmd.level}; lower license ranks first`,
      ),
    );
  }
  // The back door into the LL0 shape wall: the budgets above all fit at LL0 while a
  // *concentrated* rank II/III talent or trigger is held (one rank-III talent is 3
  // ranks, exactly the LL0 talent budget). Levelling DOWN to 0 must clear the same
  // per-key ceiling `set_talent_rank`/`set_skill_trigger` enforce on the way in —
  // otherwise the wall is one `set_level 1` → `set_level 0` round-trip from useless.
  const perKeyCeiling = rankCeilingAtLevel(cmd.level, MAX_TRIGGER_RANK);
  const heldTalentRank = maxHeldRank(pilot.talents);
  if (heldTalentRank > perKeyCeiling) {
    return err(
      new CommandError(
        `set_level: a talent at rank ${heldTalentRank} exceeds the rank ceiling ${perKeyCeiling} at level ${cmd.level}; an LL0 pilot takes three distinct rank-I talents — lower that talent first`,
      ),
    );
  }
  const heldSkillRank = maxHeldRank(pilot.skills);
  if (heldSkillRank > perKeyCeiling) {
    return err(
      new CommandError(
        `set_level: a skill trigger at rank ${heldSkillRank} exceeds the rank ceiling ${perKeyCeiling} at level ${cmd.level}; an LL0 pilot takes four distinct rank-I triggers — lower that trigger first`,
      ),
    );
  }
  const prev = pilot.level;
  const next: PilotData = { ...pilot, level: cmd.level };
  return ok({
    state: withPilot(state, next),
    inverse: {
      type: "set_level",
      actorId: cmd.actorId,
      pilotId: cmd.pilotId,
      level: prev,
    },
    events: changed(cmd.pilotId),
  });
}
